import { ROLE_LABELS } from "@eca/shared";
import { useQuery } from "@tanstack/react-query";
import { UserPlus } from "lucide-react";
import { useState, type FormEvent } from "react";
import { useNavigate, useParams } from "react-router";
import { AuthLayout } from "@/components/layout/auth-layout";
import { Button } from "@/components/ui/button";
import { Input, StackField } from "@/components/ui/form";
import { InfoTable } from "@/components/ui/info-table";
import { MoreLink } from "@/components/ui/more-link";
import { Notice } from "@/components/ui/notice";
import { Spinner } from "@/components/ui/panel";
import { client, errorMessage, unwrap } from "@/lib/api";
import { authClient, authErrorMessage } from "@/lib/auth-client";
import { useRefreshMe } from "@/lib/session";

export function InvitePage() {
  const { token = "" } = useParams();
  const navigate = useNavigate();
  const refresh = useRefreshMe();
  const invite = useQuery({ queryKey: ["invite", token], queryFn: () => unwrap(client.invitations[":token"].$get({ param: { token } })), retry: false });
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  // Set once the server has accepted; a retry after a failed sign-in must not accept again.
  const [accepted, setAccepted] = useState(false);

  if (invite.isPending) return <Spinner />;
  if (invite.isError)
    return (
      <AuthLayout title="ご招待">
        <Notice tone="bad" title="この招待リンクは使えません">{errorMessage(invite.error)}</Notice>
        <p className="mt-4 text-center"><MoreLink to="/login">ログイン画面へ</MoreLink></p>
      </AuthLayout>
    );

  const inv = invite.data;
  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    if (!inv.hasAccount) {
      if (password.length < 12) return setError("パスワードは12文字以上にしてください");
      if (password !== confirm) return setError("確認用のパスワードが一致しません");
    }
    setLoading(true);
    try {
      if (!accepted) {
        await unwrap(client.invitations[":token"].accept.$post({ param: { token }, json: { password: inv.hasAccount ? null : password } }));
        setAccepted(true);
      }
      if (inv.hasAccount) return navigate("/login", { replace: true });
      const { error } = await authClient.signIn.email({ email: inv.email, password });
      if (error) return setError(authErrorMessage(error));
      await refresh();
      navigate("/setup-2fa", { replace: true });
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthLayout title="ご招待" lead={`${inv.name} 様、Expert Column AI へようこそ。`}>
      <InfoTable
        className="mb-5"
        rows={[
          { label: "メールアドレス", value: inv.email },
          { label: "役割", value: ROLE_LABELS[inv.role] },
          ...(inv.officeName ? [{ label: "事務所", value: inv.officeName }] : []),
        ]}
      />
      <form noValidate onSubmit={submit} className="space-y-4">
        {accepted && <Notice tone="ok">アカウントを作成しました。続けてログインしてください。</Notice>}
        {error && <Notice tone="bad">{error}</Notice>}
        {inv.hasAccount ? (
          <p className="text-sm text-ink-2">このメールアドレスのアカウントはすでにあります。招待を受け入れたあと、いつものパスワードでログインしてください。</p>
        ) : (
          <>
            <StackField label="パスワード" required hint="12文字以上。ほかのサービスと同じものは避けてください。">
              {(id) => <Input id={id} type="password" autoComplete="new-password" value={password} onChange={(e) => setPassword(e.target.value)} />}
            </StackField>
            <StackField label="パスワード（確認）" required>
              {(id) => <Input id={id} type="password" autoComplete="new-password" value={confirm} onChange={(e) => setConfirm(e.target.value)} />}
            </StackField>
          </>
        )}
        <Button type="submit" size="lg" className="w-full" loading={loading}>
          <UserPlus />
          {accepted ? "ログインする" : "招待を受け入れる"}
        </Button>
      </form>
    </AuthLayout>
  );
}
